import { usePathHelper } from '@/hooks/usePathHelper'
import { cn } from '@/lib/utils'
import { ExternalLinkIcon, Star } from 'lucide-react'
import { useMemo } from 'react'
import LinkCard from '@/components/patterns/link-card'
import LinkCardAction from '@/components/patterns/link-card-action'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { CardContent, CardDescription, CardTitle } from '../ui/card'
import { Typography } from '@/components/ui/typography'
import { getStripePaymentMethodDomainStatusMeta } from './stripe-payment-method-domain-status'

function DomainCard({ domain }) {
  const pathHelper = usePathHelper()
  const getUrlObject = (url) => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
      const domainName = urlObj.hostname.split('.').slice(0, -1).join('.')

      return { origin: urlObj.origin, hostname: urlObj.hostname, domainName }
    } catch {
      return { hostname: url, origin: url }
    }
  }

  const urlObj = useMemo(() => getUrlObject(domain.domain), [domain.domain])
  const { hostname, origin } = urlObj
  const stripeStatusMeta = getStripePaymentMethodDomainStatusMeta(
    domain?.stripe_payment_method_domain
  )
  const isVerified = Boolean(domain?.verified_at)
  const isPrimary = Boolean(domain?.is_primary)

  return (
    <LinkCard
      {...pathHelper.domains.show(domain.id).link}
      className="border-border bg-card shadow-none"
    >
      <CardContent className="space-y-5 px-5 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {isPrimary ? (
                <Badge variant="secondary" className="gap-1">
                  <Star className="size-3 fill-current" />
                  Primary
                </Badge>
              ) : null}
              <Badge variant={isVerified ? 'success' : 'warning'}>
                {isVerified ? 'Connected' : 'Needs setup'}
              </Badge>
              <Badge variant={stripeStatusMeta.variant}>{stripeStatusMeta.label}</Badge>
            </div>
            <div className="space-y-1">
              <CardTitle className="truncate text-xl sm:text-2xl">{hostname}</CardTitle>
              <CardDescription className="text-sm">
                {isVerified
                  ? 'This address is connected to the storefront.'
                  : 'Finish the setup steps before customers use this address.'}
              </CardDescription>
            </div>
          </div>

          <LinkCardAction onClick={() => window.open(origin, '_blank')} disabled={!isVerified}>
            <Button variant={isVerified ? 'outline' : 'ghost'}>
              {isVerified ? 'View Storefront' : 'Waiting for setup'}
              <ExternalLinkIcon className="size-4" />
            </Button>
          </LinkCardAction>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl bg-muted/45 p-3">
            <Typography variant="labelStrong" tone="muted">
              Connection
            </Typography>
            <p className={cn('mt-1 text-sm font-semibold', isVerified && 'text-success')}>
              {isVerified ? 'Ready for shoppers' : 'Setup not complete'}
            </p>
          </div>
          <div className="rounded-xl bg-muted/45 p-3">
            <Typography variant="labelStrong" tone="muted">
              Storefront address
            </Typography>
            <p className="mt-1 truncate text-sm font-semibold">{origin}</p>
          </div>
          <div className="rounded-xl bg-muted/45 p-3">
            <Typography variant="labelStrong" tone="muted">
              Payments
            </Typography>
            <p className="mt-1 text-sm font-semibold">{stripeStatusMeta.label}</p>
          </div>
        </div>
      </CardContent>
    </LinkCard>
  )
}

export default DomainCard
